import * as React from 'react';

import type {
    EntityAttribute,
    LeadGoalFragment_goalConditions as GoalFragment,
} from 'nutshell-graphql-types';

import type {FieldError} from '../../../../../../ui/core-entity/hooks/use-custom-field-handlers';

import {WidgetErrorBanner} from '../../card/card-widget/widget-error-banner';
import {colors} from '../../colors/colors';
import {CoreEntityConfirmationDialog} from '../../core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {StageGoalIcon} from '../../icon/svg/stage-goal-icon';
import {useManageSaveState} from '../../saved-setting-toast/use-manage-save-state';

import {BaseRow} from '../base-row/base-row';
import type {ClickToCallPropsType} from '../custom-field-row/custom-field-row';
import {CustomFieldRowContent} from '../custom-field-row/custom-field-row-content';
import {getToolbarOptions} from '../custom-field-row/helpers';

import {GoalStatus} from './goal-status';

import styles from './goal-row.module.css';

export type Props = {
    goalCondition: GoalFragment;
    handleTextFieldUpdate: (
        newValue: string | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleEnumUpdate: (
        newValue: string[] | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleCurrencyUpdate: (
        currencyShortname: string,
        value: number | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleAddressUpdate: (
        address1: string,
        address2: string,
        address3: string,
        city: string,
        state: string,
        postalCode: string,
        country: string,
        field: EntityAttribute
    ) => Promise<any>;
    handlePhoneUpdate: (
        numberFormattedWithCountryCode: string,
        extension: string,
        field: EntityAttribute
    ) => Promise<any>;
    clickToCallProps?: ClickToCallPropsType | null | undefined;
    error: string | null | undefined;
    setErrorForField: (fieldError: FieldError) => void;
};

export function GoalRow(props: Props) {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [isClearConfirmationOpen, setIsClearConfirmationOpen] = React.useState<boolean>(false);
    const {toastState, saveFailed} = useManageSaveState(isSaving, 1000);

    const clearButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const customField = props.goalCondition.conditionCustomField;

    const ctcProps = {
        anchorElementId: `tertiary-action-button-${props.goalCondition.condition.id}`,
        ...props.clickToCallProps,
    } as const;

    const handleClear = () => {
        if (customField) {
            setIsSaving(true);
            props.handleTextFieldUpdate(null, customField.node).finally(() => setIsSaving(false));
        }
    };

    const handleClearClicked = () => {
        setIsClearConfirmationOpen(true);
    };

    const getClearButtonRef = (ref?: HTMLElement | null) => {
        clearButtonRef.current = ref;
    };

    const toolbarOptions = customField
        ? getToolbarOptions(
              customField.node,
              customField.value,
              // @ts-expect-error
              ctcProps,
              setIsEditing,
              handleClearClicked,
              getClearButtonRef
          )
        : [];

    React.useEffect(() => {
        if (props.error) {
            setIsSaving(false);
            saveFailed();
        }
    }, [props.error, saveFailed]);

    const setError = (newError?: string | null) => {
        if (customField) {
            props.setErrorForField({
                fieldName: customField.node.attribute.name,
                error: newError,
            });
        }
    };

    return (
        <>
            {props.error && (
                <div className='pad-8 pb-2'>
                    <WidgetErrorBanner error={props.error} setError={setError} />
                </div>
            )}
            <BaseRow
                key={props.goalCondition.condition.id}
                type='goal'
                title={props.goalCondition.condition.humanReadableObjectiveString}
                saveToastState={toastState}
                onClick={customField ? () => setIsEditing(true) : undefined}
                toolbarProps={{
                    options: toolbarOptions,
                    forceHideToolbar: isEditing,
                    forceShowToolbar: isClearConfirmationOpen,
                }}
                backgroundColor={props.goalCondition.status === 'FULFILLED' ? 'green' : 'default'}
                customIcon={
                    <div className={styles['goal-row-icon-container']}>
                        <StageGoalIcon
                            size={16}
                            // @ts-expect-error
                            color={
                                props.goalCondition.status === 'FULFILLED' ? colors.lead : undefined
                            }
                        />
                    </div>
                }
            >
                {props.goalCondition.conditionCustomField ? (
                    <CustomFieldRowContent
                        field={
                            props.goalCondition.conditionCustomField &&
                            props.goalCondition.conditionCustomField.node
                        }
                        value={
                            props.goalCondition.conditionCustomField &&
                            props.goalCondition.conditionCustomField.value
                        }
                        handleTextFieldUpdate={props.handleTextFieldUpdate}
                        handleEnumUpdate={props.handleEnumUpdate}
                        handleCurrencyUpdate={props.handleCurrencyUpdate}
                        handleAddressUpdate={props.handleAddressUpdate}
                        handlePhoneUpdate={props.handlePhoneUpdate}
                        setIsSaving={setIsSaving}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                    />
                ) : (
                    <></>
                )}
                <GoalStatus status={props.goalCondition.status} />
            </BaseRow>
            {isClearConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={clearButtonRef.current}
                    type='customField'
                    parentEntity='lead'
                    onConfirm={() => {
                        setIsClearConfirmationOpen(false);
                        handleClear();
                    }}
                    onCancel={() => {
                        setIsClearConfirmationOpen(false);
                    }}
                />
            )}
        </>
    );
}
